import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import type { Company, Contact } from '@paintscout/api'
import type { Theme } from '@material-ui/core/styles'
import { GoogleMap } from '@react-google-maps/api'
import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { StyleClasses } from '@paintscout/ui/styles'
import { Collapse, IconButton, Paper, Typography, useGoogleMaps } from '@paintscout/ui'
import { LocationIcon } from '@paintscout/ui/icons'

const JUSTIFY_FLEX_START = 'flex-start'
const STREET_VIEW_ID = 'street-view'

const useStyles = makeStyles<Theme, MapContainerProps>(
  (theme) => ({
    root: {},
    mapContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: JUSTIFY_FLEX_START
    },
    mapHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
      paddingLeft: theme.spacing(1.5),
      height: '4.5rem',
      paddingRight: theme.spacing(0.5)
    },
    mapBody: {
      padding: `${theme.spacing(0)}px ${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(2)}px`
    },
    map: {
      height: '235px',
      widht: '607px',
      borderRadius: theme.borderRadius.md
    },
    rotate: {
      transform: 'rotate(180deg)',
      transition: 'transform 0.5s ease-in-out'
    },
    expandIcon: {
      marginTOp: `-${theme.spacing(0.5)}`,
      transform: 'rotate(0deg)',
      transition: 'transform 0.5s ease-in-out'
    },
    mapIcon: {
      fontSize: '1.2rem',
      marginTop: `-${theme.spacing(0.5)}`
    },
    mapIconContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: JUSTIFY_FLEX_START
    },
    expandIconContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: JUSTIFY_FLEX_START,
      height: '100%',
      paddingTop: theme.spacing(1.25)
    },
    mapHeaderText: {
      flexGrow: 1,
      marginLeft: theme.spacing(1),
      overflow: 'hidden',
      '&:hover': {
        color: theme.paintscout.palette.primary,
        '& > *': {
          color: theme.paintscout.palette.primary
        }
      }
    },
    mapHeaderSubText: {
      color: theme.palette.text.primary
    },
    errorBody: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '50px',
      paddingBottom: theme.spacing(3),
      width: '100%'
    }
  }),
  { name: 'MapContainer' }
)

export interface MapContainerProps {
  classes?: StyleClasses<typeof useStyles>
  item: Contact | Company
  geocode?: { lat: number; lng: number }
}

function determineCollapsedState(item: Contact | Company, geocode?: { lat: number; lng: number }) {
  if (!item || !geocode) return true
  const storedValue = localStorage.getItem(`mapCollapsed-${item._id}`)
  if (storedValue !== null) {
    return storedValue === 'true'
  }
  return false
}

function MapContainer(props: MapContainerProps) {
  const { item } = props
  const classes = useStyles(props)
  const { isLoaded } = useGoogleMaps()
  const fullAddress = [item?.address, item?.city, item?.province, item?.postal]
    .filter(Boolean)
    .join(', ')
    .replace(/,\s*$/, '')
  const additionalAddress = [item?.city, item?.province, item?.postal].filter(Boolean).join(', ')
  const streetAddress = item?.address || ''
  const geocode = item?.address ? props.geocode : null
  const [collapsed, setCollapsed] = useState(determineCollapsedState(item, geocode))

  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null)
  const mapRef = React.useRef<google.maps.Map | null>(null)
  const [noStreetView, setNoStreetView] = useState(false)
  const containerStyle = {
    maxWidth: '',
    maxHeight: '250px'
  }

  function openMap(address) {
    const encodedAddress = encodeURIComponent(address)
    if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
      window.open(`maps://?q=${encodedAddress}`, '_blank')
    } else {
      window.open(`https://www.google.com/maps/search/${encodedAddress}`, '_blank')
    }
  }

  const handleCollapseClick = () => {
    setCollapsed(!collapsed)
    localStorage.setItem(`mapCollapsed-${item._id}`, (!collapsed).toString())
  }

  useEffect(() => {
    setCollapsed(determineCollapsedState(item, geocode))
  }, [item?._id, geocode?.lat, geocode?.lng])

  useEffect(() => {
    if (isLoaded && !collapsed && geocode) {
      setCenter(geocode)
    }
  }, [isLoaded, fullAddress, collapsed])

  useEffect(() => {
    // Only run if center is set and not the default (0,0)
    if (
      isLoaded &&
      !collapsed &&
      center &&
      !(center.lat === 0 && center.lng === 0) &&
      document.getElementById(STREET_VIEW_ID)
    ) {
      const streetViewService = new google.maps.StreetViewService()
      streetViewService.getPanorama({ location: center, radius: 50 }, (data, status) => {
        if (status === google.maps.StreetViewStatus.OK) {
          setNoStreetView(false)
          const heading = google.maps.geometry.spherical.computeHeading(
            data.location.latLng,
            new google.maps.LatLng(center.lat, center.lng)
          )
          new google.maps.StreetViewPanorama(document.getElementById(STREET_VIEW_ID) as HTMLElement, {
            position: center,
            pov: { heading, pitch: 0 },
            zoom: 1
          })
        } else {
          setNoStreetView(true)
        }
      })
    }
  }, [isLoaded, collapsed, center])

  const getNotFoundMessage = () => {
    if (!fullAddress) {
      return 'No address found to display on the map.'
    } else {
      return 'Unable to load this address on the map.'
    }
  }

  return (
    <Paper className={classes.mapContainer}>
      <div className={classes.mapHeader} style={!streetAddress && !additionalAddress ? { display: 'none' } : {}}>
        <div className={classes.mapIconContainer}>
          <LocationIcon className={classes.mapIcon} />
        </div>
        <div className={classes.mapHeaderText} onClick={() => openMap(fullAddress)}>
          <Typography variant={'h4'}>{streetAddress}</Typography>
          <Typography variant={'subtitle1'} className={classes.mapHeaderSubText}>
            {additionalAddress}
          </Typography>
        </div>
        {geocode && !noStreetView && (
          <div className={classes.expandIconContainer}>
            <IconButton onClick={handleCollapseClick} disabled={!fullAddress}>
              <ExpandMoreIcon
                className={classNames({ [classes.expandIcon]: !collapsed, [classes.rotate]: collapsed })}
              />
            </IconButton>
          </div>
        )}
      </div>
      {(!geocode || noStreetView) && (
        <div className={classes.errorBody} style={!geocode ? { paddingTop: 32, paddingBottom: 32 } : {}}>
          <Typography variant={'subtitle1'}>{getNotFoundMessage()}</Typography>
        </div>
      )}
      <Collapse show={!collapsed && !noStreetView}>
        <div className={classes.mapBody}>
          <div id={STREET_VIEW_ID} className={classes.map}>
            {isLoaded && !collapsed && (
              <GoogleMap
                options={{ disableDefaultUI: false, streetViewControl: true }}
                mapContainerStyle={containerStyle}
                center={center}
                onLoad={(map) => {
                  mapRef.current = map
                }}
              />
            )}
          </div>
        </div>
      </Collapse>
    </Paper>
  )
}

export default MapContainer
